import React, { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export type TabbedSectionTab = {
  id: string;
  label: string;
  badge?: string | number;
  /**
   * Flags the tab as needing attention — renders it in brand-red, the same
   * highlight the pro uses for a blown deadline (per-diem past due). Set it
   * for a condition someone has to act on, not merely a non-zero count: the
   * badge already carries "how many", this carries "and some are live".
   *
   * The two are independent on purpose. `ROs 5` can be flagged with only one
   * of those five still open, so `alertTitle` should say which.
   */
  alert?: boolean;
  /** Tooltip on a flagged tab, naming what the flag means. */
  alertTitle?: string;
};

type Props = {
  tabs: TabbedSectionTab[];
  /** Render-prop body — receives the active tab id. */
  children: (activeId: string) => React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  /** Optional Section-style title rendered above the tab strip. When
   *  provided, the collapse chevron moves into this header and the
   *  redundant tab-strip chevron is hidden. */
  title?: string;
  icon?: React.ReactNode;
  /** Optional controlled active tab id. When provided the parent owns which
   *  tab shows (pair it with `onTabChange`); omit for self-managed state. */
  activeId?: string;
  /** Fires with the clicked tab id. Required for controlled use. */
  onTabChange?: (id: string) => void;
};

/**
 * Section variant whose header is an internal tab strip. Tracks the active
 * tab id and a collapsed/open state. Mirrors the prototype's `TabbedSection`
 * (single-file lines 1526-1632). Pass `title` (and optionally `icon`) to
 * surface a Section-style collapsible header above the tabs.
 */
const TabbedSection: React.FC<Props> = ({
  tabs,
  children,
  defaultOpen = true,
  className,
  title,
  icon,
  activeId,
  onTabChange,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [internalTab, setInternalTab] = useState<string>(tabs[0]?.id ?? '');
  const activeTab = activeId ?? internalTab;
  const selectTab = (id: string) => {
    if (activeId === undefined) setInternalTab(id);
    onTabChange?.(id);
  };

  return (
    <div
      className={`px-4 py-1.5 min-w-0 flex flex-col ${className ?? ''}`}
    >
      {title && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className={
            'flex w-full items-center justify-between gap-2 py-1 select-none ' +
            (open
              ? 'border-b border-dashed border-slate-200 dark:border-gray-700 mb-1.5'
              : '')
          }
        >
          <span className="inline-flex items-center gap-1.5 text-[0.65625rem] font-semibold uppercase tracking-wider text-slate-400 dark:text-gray-500">
            {icon && (
              <span className="text-blue-600 dark:text-blue-400 inline-flex">
                {icon}
              </span>
            )}
            {title}
          </span>
          <ChevronDownIcon
            className={`h-3 w-3 text-slate-400 dark:text-gray-500 transition-transform duration-150 ${open ? '' : '-rotate-90'}`}
            strokeWidth={2.5}
          />
        </button>
      )}
      {open && (
        <>
          <div className="flex items-end justify-between border-b border-slate-300 dark:border-gray-600 select-none">
            <div role="tablist" className="flex items-end gap-0.5 flex-1 min-w-0">
              {tabs.map((t) => {
                const isActive = activeTab === t.id;
                // A flagged tab keeps the active/inactive distinction rather
                // than going solid red in both states: filled red while
                // inactive, where it has to pull the eye, and red-on-white
                // while active, so the selected tab still reads as joined to
                // the panel below it (that white bottom border is what makes
                // the tab look open).
                const alerted = !!t.alert;
                const tabTone = alerted
                  ? (isActive
                      ? 'bg-white dark:bg-gray-900 text-brand-red dark:text-red-400 font-bold border-brand-red dark:border-red-500 border-b-white dark:border-b-gray-900'
                      : 'bg-brand-red text-white font-bold border-brand-red')
                  : (isActive
                      ? 'bg-white dark:bg-gray-900 text-slate-800 dark:text-gray-100 font-bold border-b-white dark:border-b-gray-900'
                      : 'bg-slate-100 dark:bg-gray-800 text-slate-400 dark:text-gray-500 font-semibold');
                const badgeTone = alerted
                  ? (isActive
                      ? 'bg-brand-red text-white'
                      : 'bg-white/25 text-white')
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300';
                return (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    key={t.id}
                    title={alerted ? t.alertTitle : undefined}
                    onClick={() => selectTab(t.id)}
                    className={
                      '-mb-px px-2.5 py-1.5 text-[0.65625rem] uppercase tracking-wider whitespace-nowrap ' +
                      'inline-flex items-center gap-1.5 ' +
                      'border border-slate-300 dark:border-gray-600 rounded-t-md ' +
                      tabTone
                    }
                  >
                    <span>{t.label}</span>
                    {t.badge != null && t.badge !== '' && (
                      <span className={`text-[0.5625rem] px-1.5 rounded-full font-bold leading-tight ${badgeTone}`}>
                        {t.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {!title && (
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label="Collapse section"
                aria-expanded={open}
                className="text-slate-400 dark:text-gray-500 inline-flex p-1"
              >
                <ChevronDownIcon
                  className="h-3 w-3 transition-transform duration-150"
                  strokeWidth={2.5}
                />
              </button>
            )}
          </div>
          <div className="border border-t-0 border-slate-300 dark:border-gray-600 rounded-b-md p-2 flex-1 flex flex-col">
            {children(activeTab)}
          </div>
        </>
      )}
    </div>
  );
};

export default TabbedSection;
